/**
 * Reads the current lock state for a plan.
 *
 * Polls only while the user is NOT holding the session: in view mode this is
 * what surfaces the holder in the footer and re-enables "Edit plan" once the
 * lock frees. While editing, the `extend` heartbeat already returns the
 * authoritative state, so polling here as well would ask the same question
 * twice.
 *
 * Uses `useQuery` directly rather than `useCommonApiHandler`, which converts a
 * rejected request into a resolved `{ data: { error } }`. That would make a
 * failed meta call indistinguishable from "the plan is free".
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { toEditLockSnapshot, EMPTY_SNAPSHOT } from '../../editLockAdapter';
import { readCurrentUserEmail } from '../../editLockStorage';
import type { EditLockResponse, EditLockSnapshot } from '../../types';

const VIEW_MODE_POLL_MS = 30_000;

const fetchEditLockMeta = async (planId: string): Promise<EditLockSnapshot> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios<EditLockResponse>(tpoGenUrls.getPlanEditMeta(planId), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
    });
    return toEditLockSnapshot(response.data, Date.now(), readCurrentUserEmail());
};

interface UseEditLockMetaOptions {
    planId: string | undefined;
    /** False disables the whole feature (config kill-switch). */
    enabled: boolean;
}

export function useEditLockMeta({ planId, enabled }: UseEditLockMetaOptions) {
    const { data, isPending, isError } = useQuery({
        queryKey: tpoGenKeys.editLock(planId ?? ''),
        queryFn: () => fetchEditLockMeta(planId as string),
        enabled: enabled && !!planId,
        // Self-regulating: the poll only runs while someone else could be
        // holding the lock. Once this user owns it, the `extend` heartbeat is
        // the authoritative signal and polling would duplicate it.
        refetchInterval: (query) => (query.state.data?.isMine ? false : VIEW_MODE_POLL_MS),
        refetchOnWindowFocus: true,
        // The lease is time-sensitive; a cached snapshot is never reusable.
        staleTime: 0,
        retry: 1,
    });

    return {
        // On error, fall back to "free" for display but never to "mine": the
        // mutation guards remain the real gate, and acquire re-checks anyway.
        snapshot: data ?? EMPTY_SNAPSHOT,
        isPending,
        isError,
    };
}

export { VIEW_MODE_POLL_MS };
